import React, { memo } from 'react';
import { UsinaEolicaDenormalizada } from 'helper/windx';

interface LinhaUsinaProps {
  usina: UsinaEolicaDenormalizada;
  selecionada: boolean;
  onToggle: (id: number) => void;
  // Altura fixa imposta pelo virtualizer (px). Mantemos no React em vez
  // de no CSS porque o virtualizer ja injeta `transform` no wrapper —
  // height precisa bater com `estimateSize` (36 atualmente).
  altura: number;
  // Translate Y dado pelo virtualizer.
  translateY: number;
}

/**
 * Linha individual da tabela virtualizada. Memoizada com `React.memo` e
 * comparacao por id + selecionada + translateY — assim toggles em outras
 * linhas nao causam re-render desta.
 */
function LinhaUsina({
  usina,
  selecionada,
  onToggle,
  altura,
  translateY,
}: LinhaUsinaProps) {
  // Linha inteira fica clickable via <label> aninhado em volta do checkbox —
  // sem onClick na div externa (evita conflito jsx-a11y para role="row").
  // O clique em qualquer celula propaga para o checkbox via `for=inputId`.
  const inputId = `windx-usina-${usina.id}`;
  return (
    <label
      htmlFor={inputId}
      aria-selected={selecionada}
      className={`windx-seletor-usinas__linha${selecionada ? ' is-selecionada' : ''}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: `${altura}px`,
        transform: `translateY(${translateY}px)`,
      }}
    >
      <div role="cell" className="windx-seletor-usinas__cell windx-seletor-usinas__cell--check">
        <input
          id={inputId}
          type="checkbox"
          className="form-check-input"
          checked={selecionada}
          onChange={() => onToggle(usina.id)}
          aria-label={`Selecionar ${usina.nome}`}
        />
      </div>
      <div role="cell" className="windx-seletor-usinas__cell windx-seletor-usinas__cell--codigo">
        {usina.codigo}
      </div>
      <div role="cell" className="windx-seletor-usinas__cell windx-seletor-usinas__cell--nome">
        {usina.nome}
      </div>
      <div role="cell" className="windx-seletor-usinas__cell windx-seletor-usinas__cell--ceg">
        {usina.nucleo_ceg ?? '—'}
      </div>
      <div role="cell" className="windx-seletor-usinas__cell windx-seletor-usinas__cell--potencia">
        {usina.potencia.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
      <div role="cell" className="windx-seletor-usinas__cell windx-seletor-usinas__cell--uf">
        {usina.uf_sigla ?? '—'}
      </div>
      <div role="cell" className="windx-seletor-usinas__cell windx-seletor-usinas__cell--sub">
        {usina.subsistema_sm_sigla ?? '—'}
      </div>
    </label>
  );
}

export default memo(LinhaUsina, (prev, next) => (
  prev.usina.id === next.usina.id
  && prev.selecionada === next.selecionada
  && prev.altura === next.altura
  && prev.translateY === next.translateY
));
